import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  Optional,
  SkipSelf
} from '@angular/core';
import { ControlContainer, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { debounceTime, takeUntil } from 'rxjs/operators';

import { DisplaySchema, SummitDisposing, SummitIdObject, ToCCase, ViewField } from '@summit/models';
import { ApiError } from '@summit/ui';

import { SFormError } from '../../directives';

@Component({
  selector: 's-form-error-total',
  templateUrl: './s-form-error-total.component.html',
  standalone: true,
  imports: [ReactiveFormsModule, SFormError],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormErrorTotalComponent<T extends SummitIdObject> extends SummitDisposing implements OnInit {
  @Input() id: string;
  @Input() apiError: ApiError;
  @Input() form: FormGroup;
  @Input() schema: DisplaySchema<T> | false;
  formErrors: string[][];
  displayHtmlErrors = false;
  unknownErrorKeys: [string, string[]][] = [];

  constructor(
    /*noUnusedParameters private viewportScroller: ViewportScroller,*/ private cd: ChangeDetectorRef,
    @Optional() @SkipSelf() private controlContainer: ControlContainer
  ) {
    super();
    this.form ??= this.controlContainer?.control as FormGroup;
  }

  ngOnInit() {
    if (!this.form) {
      console.error(`No Form was provided to Form Error Total`);
    } else {
      this.form.statusChanges.pipe(debounceTime(50), takeUntil(this.disposing$)).subscribe(() => {
        if (this.displayErrors()) {
          this.updateFormErrors();
          this.displayHtmlErrors = true;
        } else {
          this.displayHtmlErrors = false;
        }
        this.updateFormErrors();
        this.cd.detectChanges();
      });
    }
    this.updateFormErrors();
  }

  displayErrors(): boolean {
    return (
      this.form?.invalid &&
      (this.form.dirty || (this.form.touched && this.notFormFocus())) &&
      !this.form.pending &&
      Object.keys(this.form.controls)
        .filter((controlKey) => this.form.controls[controlKey].invalid)
        .filter((controlKey) => this.form.controls[controlKey].touched || this.form.controls[controlKey].dirty).length >
        0
    );
  }

  updateFormErrors() {
    const response = new Array<Array<string>>();
    if (this.form?.controls) {
      //console.log('Process errors');
      Object.keys(this.form.controls)
        .filter((controlKey) => this.form.controls[controlKey].errors)
        .sort((n1, n2) => {
          if (n1 > n2) {
            return 1;
          }
          if (n1 < n2) {
            return -1;
          }
          return 0;
        })
        .forEach((controlKey) => {
          //console.log('Processing control: ', controlKey, this.form.controls[controlKey]);
          if (this.form.controls[controlKey].touched || this.form.controls[controlKey].dirty) {
            const attribute: ViewField<T> | null | undefined = this.schema
              ? this.schema?.getAttribute(controlKey as keyof T & string)
              : null;
            const serverErrorValue: unknown = this.apiError?.model
              ? (this.apiError?.model[controlKey] ?? this.apiError?.model[ToCCase(controlKey)])
              : null;
            const serverError = Array.isArray(serverErrorValue)
              ? serverErrorValue.join(', ')
              : (serverErrorValue?.toString() ?? null);
            const controlErrors = this.form.controls[controlKey].errors;
            if (attribute) {
              if (!controlErrors) {
                return;
              }
              Object.keys(controlErrors).forEach((errorKey) => {
                if (errorKey === 'apiError' && serverError) {
                  //console.log(`Processing ${errorKey} control as Api Error`, this.apiError.model);
                  response.push([controlKey, attribute.displayName ?? ToCCase(controlKey), serverError]);
                } else if (attribute && attribute.validationMessages) {
                  //console.log(`Processing ${errorKey} control as Lcl Error`);
                  const validationMessage = attribute.validationMessages.find((entry) => entry.type === errorKey);
                  if (validationMessage) {
                    response.push([
                      controlKey,
                      attribute.displayName ?? ToCCase(controlKey),
                      validationMessage.message ?? errorKey
                    ]);
                  }
                }
              });
            } else if (serverError) {
              response.push([controlKey, ToCCase(controlKey), serverError]);
            }
          }
        });
    }
    //console.log(`response array: `, response);
    this.formErrors = response;
    this.cd.detectChanges();
  }

  jumpTo(anchor: string) {
    this.form?.markAllAsTouched();
    // console.log(`Got fragment update: ${anchor}`);
    //*noUnusedParameters const temp = document.getElementById(anchor);
    // console.log(`Got fragment update: ${anchor}`, temp);
    const anchorElement = anchor ? document.getElementById(anchor) : null;
    if (anchorElement) {
      // console.log(`Scrolling to: ${anchor}`);
      anchorElement.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
        inline: 'nearest'
      });
    } else {
      // console.log(`Tried to Scrolling to: ${anchor} but failed`);
    }
  }

  notFormFocus(): boolean {
    const currentElement = document.activeElement;
    if (currentElement && currentElement.classList.contains('form-control')) {
      return false;
    }
    // console.log(`Focus element = `, currentElement, currentElement?.classList);
    return true;
  }
}
